/**
 * Card Component
 * Glass morphism card with variants and hover effects
 */

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { glassPanel, glassPanelElevated, scrollbarThin } from '../mixins';
import { spacing, typography, transitions, borderRadius, shadows } from '../tokens';
import { media } from '../responsive';

type CardVariant = 'default' | 'elevated' | 'outlined' | 'ghost';
type CardSize = 'sm' | 'md' | 'lg';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  variant?: CardVariant;
  size?: CardSize;
  accentBorder?: boolean;
  accentColor?: string;
  interactive?: boolean;
  disableHover?: boolean;
  fullHeight?: boolean;
  className?: string;
}

const getPadding = (size: CardSize): string => {
  switch (size) {
    case 'sm':
      return spacing['3'];
    case 'lg':
      return spacing['6'];
    default:
      return spacing['4'];
  }
};

const CardWrapper = styled.div<{
  $variant: CardVariant;
  $size: CardSize;
  $accentColor?: string;
  $accentBorder: boolean;
  $interactive: boolean;
  $disableHover: boolean;
  $fullHeight: boolean;
}>`
  ${(props) => {
    switch (props.$variant) {
      case 'elevated':
        return glassPanelElevated;
      case 'outlined':
        return css`
          background: ${props =>
            props.theme?.colors?.background?.secondary || 'rgba(20, 20, 20, 0.6)'};
          border: 1px solid ${props =>
            props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
          border-radius: ${borderRadius.lg};
        `;
      case 'ghost':
        return css`
          background: transparent;
          border: none;
          border-radius: ${borderRadius.lg};
        `;
      default:
        return glassPanel;
    }
  }}

  padding: ${(props) => getPadding(props.$size)};
  transition: ${transitions.all};
  border-radius: ${borderRadius.lg};

  /* Responsive padding for smaller screens */
  ${media.belowLg} {
    padding: ${(props) => {
      switch (props.$size) {
        case 'sm':
          return spacing['2'];
        case 'lg':
          return spacing['5'];
        default:
          return spacing['3'];
      }
    }};
  }

  ${media.belowMd} {
    padding: ${(props) => {
      switch (props.$size) {
        case 'sm':
          return spacing['2'];
        case 'lg':
          return spacing['4'];
        default:
          return spacing['3'];
      }
    }};
    border-radius: ${borderRadius.md};
  }

  ${(props) =>
    props.$fullHeight &&
    css`
      height: 100%;
      display: flex;
      flex-direction: column;
    `}

  ${(props) =>
    props.$accentBorder &&
    css`
      border-top: 3px solid ${props.$accentColor || props.theme?.colors?.accent?.primary || '#32828B'};
    `}

  ${(props) =>
    props.$interactive &&
    css`
      cursor: pointer;
    `}

  ${(props) =>
    !props.$disableHover &&
    css`
      &:hover {
        ${props.$variant === 'elevated'
          ? css`
              transform: translateY(-2px);
              box-shadow: ${shadows.lg};
            `
          : props.$variant === 'outlined'
          ? css`
              border-color: ${props.$accentColor || props.theme?.colors?.accent?.primary || '#32828B'};
            `
          : css`
              background: ${props.theme?.colors?.background?.tertiary || 'rgba(30, 30, 30, 0.8)'};
            `}
      }
    `}
`;

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'default',
      size = 'md',
      accentBorder = false,
      accentColor,
      interactive = false,
      disableHover = false,
      fullHeight = false,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <CardWrapper
        ref={ref}
        $variant={variant}
        $size={size}
        $accentColor={accentColor}
        $accentBorder={accentBorder}
        $interactive={interactive}
        $disableHover={disableHover}
        $fullHeight={fullHeight}
        className={className}
        {...props}
      >
        {children}
      </CardWrapper>
    );
  }
);

Card.displayName = 'Card';

// ============================================================================
// Card Header
// ============================================================================

export interface CardHeaderProps {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

const CardHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing['3']};
  margin-bottom: ${spacing['4']};
  padding-bottom: ${spacing['3']};
  border-bottom: 1px solid ${props =>
    props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};

  ${media.belowMd} {
    gap: ${spacing['2']};
    margin-bottom: ${spacing['3']};
    padding-bottom: ${spacing['2']};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['3']};
`;

const HeaderIcon = styled.div`
  width: 36px;
  height: 36px;
  background: ${props =>
    `${props.theme?.colors?.accent?.primary || '#32828B'}20`};
  border-radius: ${borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    color: ${props =>
      props.theme?.colors?.accent?.primary || '#32828B'};
  }

  ${media.belowMd} {
    width: 32px;
    height: 32px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const HeaderText = styled.div``;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${props => props.theme?.colors?.text?.primary || '#ffffff'};
  line-height: 1.3;

  ${media.belowLg} {
    font-size: ${typography.fontSize.sm};
  }
`;

const HeaderSubtitle = styled.p`
  margin: 2px 0 0 0;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing['2']};
`;

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  subtitle,
  icon,
  actions,
  className,
}) => {
  return (
    <CardHeaderWrapper className={className}>
      <HeaderContent>
        {icon && <HeaderIcon>{icon}</HeaderIcon>}
        <HeaderText>
          <HeaderTitle>{title}</HeaderTitle>
          {subtitle && <HeaderSubtitle>{subtitle}</HeaderSubtitle>}
        </HeaderText>
      </HeaderContent>
      {actions && <HeaderActions>{actions}</HeaderActions>}
    </CardHeaderWrapper>
  );
};

// ============================================================================
// Card Content
// ============================================================================

export interface CardContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  scrollable?: boolean;
  maxHeight?: string;
  className?: string;
}

const CardContentWrapper = styled.div<{
  $scrollable: boolean;
  $maxHeight?: string;
}>`
  flex: 1;
  ${(props) =>
    props.$scrollable &&
    css`
      overflow-y: auto;
      ${props.$maxHeight && `max-height: ${props.$maxHeight};`}
      ${scrollbarThin}
    `}
`;

export const CardContent: React.FC<CardContentProps> = ({
  children,
  scrollable = false,
  maxHeight,
  className,
  ...props
}) => {
  return (
    <CardContentWrapper
      $scrollable={scrollable}
      $maxHeight={maxHeight}
      className={className}
      {...props}
    >
      {children}
    </CardContentWrapper>
  );
};

// ============================================================================
// Card Footer
// ============================================================================

export interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  align?: 'left' | 'center' | 'right' | 'between';
  className?: string;
}

const getAlignment = (align: string): string => {
  switch (align) {
    case 'center':
      return 'center';
    case 'between':
      return 'space-between';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
};

const CardFooterWrapper = styled.div<{
  $align: 'left' | 'center' | 'right' | 'between';
}>`
  display: flex;
  align-items: center;
  justify-content: ${(props) => getAlignment(props.$align)};
  gap: ${spacing['3']};
  margin-top: ${spacing['4']};
  padding-top: ${spacing['3']};
  border-top: 1px solid ${props =>
    props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};

  ${media.belowMd} {
    gap: ${spacing['2']};
    margin-top: ${spacing['3']};
    padding-top: ${spacing['2']};
  }
`;

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  align = 'right',
  className,
  ...props
}) => {
  return (
    <CardFooterWrapper $align={align} className={className} {...props}>
      {children}
    </CardFooterWrapper>
  );
};

export default Card;
